import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  Trash2, 
  Plus, 
  Minus, 
  Clock, 
  MapPin,
  CreditCard,
  Banknote,
  ArrowLeft,
  ChevronRight,
  Ticket,
  Check,
  X,
  Calendar,
  CalendarDays,
  AlertCircle,
  Wallet,
  Info,
  Zap,
  Gift
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../utils/images';
import { getApiErrorMessage } from '../utils/errorHelper';

export default function Checkout() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, updateCartItem, removeFromCart, clearCart, refreshCart, isPreorderCart, preorderDelayDays, isGuestCart } = useCart();
  
  const [pickupSlots, setPickupSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Guest info
  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  
  // Reservation state
  const [pickupMode, setPickupMode] = useState('today'); // 'today' or 'reservation'
  const [reservationData, setReservationData] = useState(null);
  const [loadingReservationDates, setLoadingReservationDates] = useState(false);
  const [selectedReservationDate, setSelectedReservationDate] = useState(null);
  
  // Preorder date state
  const [preorderDates, setPreorderDates] = useState([]);
  
  // Deposit (Acompte) state
  const [depositPreview, setDepositPreview] = useState(null);
  const [loadingDeposit, setLoadingDeposit] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  
  // Automatic promotion state (Happy Hours, Packs)
  const [autoPromotion, setAutoPromotion] = useState(null);
  const [loadingAutoPromo, setLoadingAutoPromo] = useState(false);
  
  // BOGO offers state
  const [bogoOffers, setBogoOffers] = useState(null);
  const [loadingBogo, setLoadingBogo] = useState(false);

  // Online payment availability
  const [onlinePaymentDisabled, setOnlinePaymentDisabled] = useState(false);
  // Counter payment blocked (deposit required or online_only items)
  const [counterPaymentDisabled, setCounterPaymentDisabled] = useState(false);
  const [counterDisabledReason, setCounterDisabledReason] = useState('');

  // Fetch restaurant info to check online payment
  useEffect(() => {
    if (!cart.restaurant?.id) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/restaurants/${cart.restaurant.id}`);
        if (res.data.online_payment_enabled === false) {
          setOnlinePaymentDisabled(true);
          setPaymentMethod('À la récupération');
        }
      } catch { /* ignore */ }
    })();
  }, [cart.restaurant?.id]);

  // Check if cart contains online_only or deposit-required items
  useEffect(() => {
    if (!cart.items || cart.items.length === 0) {
      setCounterPaymentDisabled(false);
      return;
    }
    (async () => {
      try {
        // Fetch the full menu ONCE
        const menuRes = await axios.get(`${API}/restaurants/${cart.restaurant?.id}/menu`);
        const menu = menuRes.data?.menu || [];
        // Flatten dishes + build category map
        const allDishes = [];
        const categoryMap = {};
        for (const block of menu) {
          if (block?.category) categoryMap[block.category.id] = block.category;
          if (Array.isArray(block?.dishes)) allDishes.push(...block.dishes);
        }

        let hasOnlineOnly = false;
        let hasDeposit = false;
        let reason = '';
        for (const item of cart.items) {
          const dishId = item.dish_id || item.id;
          if (!dishId) continue;
          const dish = allDishes.find(d => d.id === dishId);
          if (!dish) continue;
          if (dish.online_only) {
            hasOnlineOnly = true;
            reason = `"${dish.name}" est uniquement disponible en paiement en ligne`;
            break;
          }
          if (dish.requires_deposit) {
            hasDeposit = true;
            reason = `"${dish.name}" necessite un acompte (paiement en ligne)`;
            break;
          }
          const cat = categoryMap[dish.category_id];
          if (cat && cat.online_only) {
            hasOnlineOnly = true;
            reason = `La categorie "${cat.name}" est en paiement en ligne uniquement`;
            break;
          }
        }
        if (hasOnlineOnly || hasDeposit) {
          setCounterPaymentDisabled(true);
          setCounterDisabledReason(reason);
          if (paymentMethod !== 'Carte') setPaymentMethod('Carte');
        } else {
          setCounterPaymentDisabled(false);
          setCounterDisabledReason('');
        }
      } catch { /* ignore */ }
    })();
  }, [cart.items, cart.restaurant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPickupSlots = useCallback(async () => {
    if (!cart.restaurant?.id) return;
    try {
      setLoadingSlots(true);
      const response = await axios.get(`${API}/restaurants/${cart.restaurant.id}/pickup-slots`);
      setPickupSlots(response.data.slots);
      if (response.data.slots.length > 0) {
        setSelectedSlot(response.data.slots[0].datetime);
      }
    } catch {
      console.error('Error fetching slots');
    } finally {
      setLoadingSlots(false);
    }
  }, [cart.restaurant?.id]);

  // Fetch reservation dates
  const fetchReservationDates = useCallback(async () => {
    if (!cart.restaurant?.id) return;
    try {
      setLoadingReservationDates(true);
      const response = await axios.get(`${API}/restaurants/${cart.restaurant.id}/reservation-dates`);
      setReservationData(response.data);
    } catch (err) {
      console.error('Error fetching reservation dates:', err);
      setReservationData({ enabled: false, dates: [] });
    } finally {
      setLoadingReservationDates(false);
    }
  }, [cart.restaurant?.id]);

  // Fetch deposit preview
  const fetchDepositPreview = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingDeposit(true);
      const response = await axios.get(`${API}/payments/deposit-preview`);
      setDepositPreview(response.data);
    } catch (err) {
      console.error('Error fetching deposit preview:', err);
      setDepositPreview({ has_deposit: false });
    } finally {
      setLoadingDeposit(false);
    }
  }, []);


  // Auto-select payment method for guests (no auto-select, force choice)
  useEffect(() => {
    if (!isAuthenticated) {
      setPaymentMethod('');
    }
  }, [isAuthenticated]);

  // Fetch automatic promotions (Happy Hours, Packs)
  const fetchAutoPromotion = useCallback(async () => {
    try {
      setLoadingAutoPromo(true);
      let response;
      if (isAuthenticated) {
        response = await axios.get(`${API}/promotions/calculate`);
      } else if (cart.items?.length > 0 && cart.restaurant?.id) {
        response = await axios.post(`${API}/promotions/calculate-guest`, {
          items: cart.items.map(i => ({ dish_id: i.dish_id || i.id, quantity: i.quantity, selected_options: i.selected_options, selected_ingredients: i.selected_ingredients })),
          restaurant_id: cart.restaurant.id
        });
      } else {
        setAutoPromotion({ has_promotion: false });
        return;
      }
      setAutoPromotion(response.data);
    } catch (err) {
      console.error('Error fetching auto promotion:', err);
      setAutoPromotion({ has_promotion: false });
    } finally {
      setLoadingAutoPromo(false);
    }
  }, [cart.items, cart.restaurant]);

  // Fetch BOGO offers
  const fetchBogoOffers = useCallback(async () => {
    try {
      setLoadingBogo(true);
      let response;
      if (isAuthenticated) {
        response = await axios.get(`${API}/bogo/calculate`);
      } else if (cart.items?.length > 0 && cart.restaurant?.id) {
        response = await axios.post(`${API}/bogo/calculate-guest`, {
          items: cart.items.map(i => ({ dish_id: i.dish_id || i.id, quantity: i.quantity, selected_options: i.selected_options, selected_ingredients: i.selected_ingredients })),
          restaurant_id: cart.restaurant.id
        });
      } else {
        setBogoOffers({ has_bogo: false, applied_offers: [], total_discount: 0 });
        return;
      }
      setBogoOffers(response.data);
    } catch (err) {
      console.error('Error fetching BOGO offers:', err);
      setBogoOffers({ has_bogo: false, applied_offers: [], total_discount: 0 });
    } finally {
      setLoadingBogo(false);
    }
  }, [cart.items, cart.restaurant]);

  useEffect(() => {
    fetchPickupSlots();
    fetchReservationDates();
    fetchDepositPreview();
    fetchAutoPromotion();
    fetchBogoOffers();
  }, [fetchPickupSlots, fetchReservationDates, fetchDepositPreview, fetchAutoPromotion, fetchBogoOffers]);

  // Force reservation mode for pre-order carts and generate available dates
  useEffect(() => {
    if (isPreorderCart && preorderDelayDays > 0) {
      setPickupMode('reservation');
      // Generate dates starting from preorderDelayDays from now
      const dates = [];
      const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
      for (let i = preorderDelayDays; i <= preorderDelayDays + 14; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        dates.push({
          date: date.toISOString().split('T')[0],
          day_name: dayNames[date.getDay()],
        });
      }
      setPreorderDates(dates);
    }
  }, [isPreorderCart, preorderDelayDays]);

  // Validate promo code
  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim(),
        restaurant_id: cart.restaurant?.id,
        order_subtotal: cart.subtotal || 0
      });
      
      setAppliedPromo(response.data);
      toast.success(`Code ${response.data.code} appliqué ! -${response.data.discount_amount.toFixed(2)}€`);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Code promo invalide');
      toast.error(message);
      setAppliedPromo(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    toast.success('Code promo retiré');
  };

  const handleQuantityChange = async (itemId, currentQty, delta) => {
    const newQty = currentQty + delta;
    try {
      await updateCartItem(itemId, newQty);
      // Reset promo if cart changes significantly
      if (appliedPromo) {
        setAppliedPromo(null);
        setPromoCode('');
      }
      // Refresh auto promotion and BOGO calculation
      fetchAutoPromotion();
      fetchBogoOffers();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await removeFromCart(itemId);
      toast.success('Article supprimé');
      // Reset promo if cart changes
      if (appliedPromo) {
        setAppliedPromo(null);
        setPromoCode('');
      }
      // Refresh auto promotion and BOGO calculation
      fetchAutoPromotion();
      fetchBogoOffers();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSubmitOrder = async () => {
    // Guest validation
    if (!isAuthenticated) {
      if (!guestFirstName.trim()) { toast.error('Veuillez entrer votre prénom'); return; }
      if (!guestLastName.trim()) { toast.error('Veuillez entrer votre nom'); return; }
      if (!guestPhone.trim()) { toast.error('Veuillez entrer votre numéro de téléphone'); return; }
    }

    if (isPreorderCart) {
      if (!selectedReservationDate) {
        toast.error('Veuillez sélectionner une date de retrait pour votre pré-commande');
        return;
      }
      if (!selectedSlot) {
        toast.error('Veuillez sélectionner un créneau horaire');
        return;
      }
    } else if (pickupMode === 'today' && !selectedSlot) {
      toast.error('Veuillez sélectionner un créneau de retrait');
      return;
    } else if (pickupMode === 'reservation') {
      if (!selectedReservationDate) {
        toast.error('Veuillez sélectionner une date de réservation');
        return;
      }
      if (!selectedSlot) {
        toast.error('Veuillez sélectionner un créneau horaire');
        return;
      }
    }

    setLoading(true);
    
    // Validate payment method selection
    if (!paymentMethod) {
      toast.error('Veuillez choisir une méthode de paiement');
      setLoading(false);
      return;
    }
    
    // Build order data
    const isReservation = isPreorderCart || pickupMode === 'reservation';
    const orderData = {
      pickup_slot: selectedSlot,
      special_instructions: specialInstructions || null,
      promo_code: appliedPromo?.code || null,
      pickup_date: isReservation ? selectedReservationDate : null,
      is_reservation: isReservation,
      auto_promotion_id: autoPromotion?.promotion?.id || null,
      auto_promotion_type: autoPromotion?.promotion?.type || null
    };
    
    try {
      // Guest order flow
      if (!isAuthenticated) {
        const guestOrderData = {
          guest_name: `${guestFirstName.trim()} ${guestLastName.trim()}`,
          guest_phone: guestPhone.trim(),
          guest_email: guestEmail.trim() || null,
          restaurant_id: cart.restaurant_id || cart.restaurant?.id,
          items: cart.items.map(item => ({
            dish_id: item.dish_id,
            quantity: item.quantity,
            selected_options: item.selected_options || null,
            selected_ingredients: item.selected_ingredients || null,
            special_instructions: item.special_instructions || null
          })),
          pickup_slot: selectedSlot,
          pickup_date: isReservation ? selectedReservationDate : null,
          special_instructions: specialInstructions || null,
          payment_method: paymentMethod,
          promo_code: appliedPromo?.code || null,
          auto_promotion_id: autoPromotion?.promotion?.id || null,
          auto_promotion_type: autoPromotion?.promotion?.type || null
        };

        if (paymentMethod === 'Carte') {
          // Guest Stripe checkout
          const checkoutResponse = await axios.post(`${API}/payments/create-guest-checkout`, {
            ...guestOrderData,
            is_reservation: isReservation
          });
          
          await clearCart();
          window.location.href = checkoutResponse.data.url;
          return;
        }

        // Guest counter payment
        const orderResponse = await axios.post(`${API}/orders/guest-create`, guestOrderData);
        
        toast.success('Commande confirmée!');
        await clearCart();
        navigate('/commande-confirmee', { state: { order: orderResponse.data } });
        return;
      }

      // Authenticated order flow
      if (paymentMethod === 'Carte') {
        localStorage.setItem('pending_checkout', JSON.stringify({
          pickup_slot: selectedSlot,
          special_instructions: specialInstructions || null,
          promo_code: appliedPromo?.code || null,
          pickup_date: isReservation ? selectedReservationDate : null,
          is_reservation: isReservation,
          auto_promotion_id: autoPromotion?.promotion?.id || null,
          auto_promotion_type: autoPromotion?.promotion?.type || null
        }));
        
        const checkoutResponse = await axios.post(`${API}/payments/create-checkout`, orderData);
        
        window.location.href = checkoutResponse.data.url;
      } else {
        const orderResponse = await axios.post(`${API}/orders/create`, {
          ...orderData,
          payment_method: paymentMethod
        });
        
        const successMessage = isReservation
          ? (isPreorderCart ? 'Pré-commande confirmée!' : 'Réservation confirmée!')
          : 'Commande confirmée!';
        toast.success(successMessage);
        await refreshCart();
        navigate(`/commandes/${orderResponse.data.id}`);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la commande'));
    } finally {
      setLoading(false);
    }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Votre panier est vide</h1>
          <p className="text-gray-500 mb-8">Ajoutez des plats depuis un restaurant pour commencer</p>
          <Button 
            onClick={() => navigate('/restaurants')}
            className="bg-emerald-500 hover:bg-emerald-600 rounded-full px-8"
          >
            Parcourir les restaurants
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showFooter={false}>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Votre commande</h1>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-3 space-y-4">
              {/* Restaurant Info */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  {cart.restaurant?.logo && (
                    <img 
                      src={getFullImageUrl(cart.restaurant.logo)} 
                      alt="" 
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                  )}
                  <div>
                    <h2 className="font-semibold text-gray-900">{cart.restaurant?.name}</h2>
                    <p className="text-sm text-gray-500 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {cart.restaurant?.address}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pre-order Banner */}
              {isPreorderCart && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="preorder-cart-banner">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <CalendarDays className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-blue-800">Panier pré-commande</p>
                      <p className="text-sm text-blue-600 mt-0.5">
                        Ce panier contient des articles en pré-commande. Vous devez sélectionner une date de retrait au minimum {preorderDelayDays} jour(s) à l'avance.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cart Items */}
              <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100">
                {cart.items.map((item) => (
                  <div key={item.id} className="p-4">
                    <div className="flex gap-4">
                      {item.dish_image && (
                        <img 
                          src={item.dish_image} 
                          alt="" 
                          className="w-20 h-20 rounded-xl object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{item.dish_name}</h3>
                            {item.preorder_only && (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full mt-1">
                                <Clock className="w-3 h-3" />
                                Pré-commande
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {item.selected_option_names && item.selected_option_names.length > 0 && (
                          <p className="text-sm text-gray-500 mt-1">
                            {item.selected_option_names.join(', ')}
                          </p>
                        )}
                        {item.selected_ingredient_names && item.selected_ingredient_names.length > 0 && (
                          <p className="text-sm text-emerald-600 mt-1">
                            🥗 {item.selected_ingredient_names.join(', ')}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity, -1)}
                              className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-6 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity, 1)}
                              className="w-8 h-8 rounded-full hover:bg-white flex items-center justify-center"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="font-semibold text-gray-900">
                            {item.total_price?.toFixed(2)} €
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pickup Mode Selection - hidden for pre-order carts (forced to reservation) */}
              {!isPreorderCart && reservationData?.enabled && (
                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-emerald-500" />
                    Type de commande
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setPickupMode('today');
                        setSelectedReservationDate(null);
                        // Reset slot to first available today
                        if (pickupSlots.length > 0) {
                          setSelectedSlot(pickupSlots[0].datetime);
                        }
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        pickupMode === 'today'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="pickup-mode-today"
                    >
                      <Clock className={`w-5 h-5 mb-2 ${pickupMode === 'today' ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${pickupMode === 'today' ? 'text-emerald-700' : 'text-gray-900'}`}>
                        Retrait aujourd'hui
                      </p>
                      <p className="text-sm text-gray-500">
                        Click & Collect rapide
                      </p>
                    </button>
                    <button
                      onClick={() => {
                        setPickupMode('reservation');
                        setSelectedSlot('');
                      }}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        pickupMode === 'reservation'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="pickup-mode-reservation"
                    >
                      <Calendar className={`w-5 h-5 mb-2 ${pickupMode === 'reservation' ? 'text-emerald-600' : 'text-gray-400'}`} />
                      <p className={`font-semibold ${pickupMode === 'reservation' ? 'text-emerald-700' : 'text-gray-900'}`}>
                        Réserver pour plus tard
                      </p>
                      <p className="text-sm text-gray-500">
                        Choisissez votre date
                      </p>
                    </button>
                  </div>
                </div>
              )}

              {/* Pre-order Date Selection */}
              {isPreorderCart && (
                <div className="bg-white rounded-2xl p-4 border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    Date de retrait (pré-commande)
                  </h3>
                  <p className="text-sm text-blue-600 mb-3">
                    Minimum {preorderDelayDays} jour(s) à l'avance
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {preorderDates.map((dateInfo) => (
                      <button
                        key={dateInfo.date}
                        onClick={() => {
                          setSelectedReservationDate(dateInfo.date);
                          setSelectedSlot('');
                        }}
                        className={`p-3 rounded-xl text-left transition-all ${
                          selectedReservationDate === dateInfo.date
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`preorder-date-${dateInfo.date}`}
                      >
                        <p className="text-xs uppercase tracking-wide opacity-70">
                          {dateInfo.day_name}
                        </p>
                        <p className="font-semibold">
                          {new Date(dateInfo.date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'short' 
                          })}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reservation Date Selection */}
              {!isPreorderCart && pickupMode === 'reservation' && reservationData?.enabled && (
                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-emerald-500" />
                    Date de retrait
                  </h3>
                  {loadingReservationDates ? (
                    <div className="text-gray-500">Chargement des dates disponibles...</div>
                  ) : !reservationData?.dates || reservationData.dates.length === 0 ? (
                    <div className="text-gray-500">Aucune date disponible</div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        {reservationData.dates.slice(0, 15).map((dateInfo) => (
                          <button
                            key={dateInfo.date}
                            onClick={() => {
                              setSelectedReservationDate(dateInfo.date);
                              setSelectedSlot('');
                            }}
                            className={`p-3 rounded-xl text-left transition-all ${
                              selectedReservationDate === dateInfo.date
                                ? 'bg-emerald-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`reservation-date-${dateInfo.date}`}
                          >
                            <p className="text-xs uppercase tracking-wide opacity-70">
                              {dateInfo.day_name}
                            </p>
                            <p className="font-semibold">
                              {new Date(dateInfo.date).toLocaleDateString('fr-FR', { 
                                day: 'numeric', 
                                month: 'short' 
                              })}
                            </p>
                          </button>
                        ))}
                      </div>
                      
                      {/* Cancellation Policy */}
                      {reservationData.cancellation_policy && (
                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                          <div className="flex gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-xs font-semibold text-amber-700 mb-1">Politique d'annulation</p>
                              <p className="text-xs text-amber-600">{reservationData.cancellation_policy}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Pickup Time */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-500" />
                  {isPreorderCart ? 'Heure de retrait souhaitée' : pickupMode === 'reservation' ? 'Heure de retrait prévue' : 'Heure de retrait'}
                </h3>
                {isPreorderCart ? (
                  // Pre-order: show time input or generic slots
                  !selectedReservationDate ? (
                    <div className="text-gray-500">Sélectionnez d'abord une date de retrait</div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500">
                        Choisissez un créneau indicatif pour le retrait
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00'].map((slot) => (
                          <button
                            key={slot}
                            onClick={() => setSelectedSlot(slot)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                              selectedSlot === slot
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            data-testid={`preorder-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                ) : pickupMode === 'today' ? (
                  // Today: show live pickup slots
                  loadingSlots ? (
                    <div className="text-gray-500">Chargement des créneaux...</div>
                  ) : pickupSlots.length === 0 ? (
                    <div className="text-gray-500">Aucun créneau disponible</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {pickupSlots.map((slot) => (
                        <button
                          key={slot.datetime}
                          onClick={() => setSelectedSlot(slot.display)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            selectedSlot === slot.display
                              ? 'bg-emerald-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          data-testid={`slot-${slot.display}`}
                        >
                          {slot.display}
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  // Reservation: show hour slots based on opening hours of selected date
                  !selectedReservationDate ? (
                    <div className="text-gray-500">Sélectionnez d'abord une date</div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-500">
                        Horaires d'ouverture : {
                          reservationData?.dates?.find(d => d.date === selectedReservationDate)?.opening_hours 
                            ? `${reservationData.dates.find(d => d.date === selectedReservationDate).opening_hours.open} - ${reservationData.dates.find(d => d.date === selectedReservationDate).opening_hours.close}`
                            : 'Non spécifiés'
                        }
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const selectedDateInfo = reservationData?.dates?.find(d => d.date === selectedReservationDate);
                          if (!selectedDateInfo?.opening_hours) return null;
                          
                          const openHour = parseInt(selectedDateInfo.opening_hours.open.split(':')[0]);
                          const closeHour = parseInt(selectedDateInfo.opening_hours.close.split(':')[0]);
                          const slots = [];
                          
                          for (let h = openHour; h < closeHour; h++) {
                            slots.push(`${String(h).padStart(2, '0')}:00`);
                            slots.push(`${String(h).padStart(2, '0')}:30`);
                          }
                          
                          return slots.map((slot) => (
                            <button
                              key={slot}
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                selectedSlot === slot
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              data-testid={`reservation-slot-${slot}`}
                            >
                              {slot}
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Guest Info Form */}
              {!isAuthenticated && (
                <div className="bg-white rounded-2xl p-4 border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4">Vos coordonnées</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="guest-firstname" className="text-sm text-gray-600">Prénom *</Label>
                        <Input
                          id="guest-firstname"
                          value={guestFirstName}
                          onChange={(e) => setGuestFirstName(e.target.value)}
                          placeholder="Prénom"
                          className="mt-1"
                          data-testid="guest-firstname"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guest-lastname" className="text-sm text-gray-600">Nom *</Label>
                        <Input
                          id="guest-lastname"
                          value={guestLastName}
                          onChange={(e) => setGuestLastName(e.target.value)}
                          placeholder="Nom"
                          className="mt-1"
                          data-testid="guest-lastname"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="guest-phone" className="text-sm text-gray-600">Téléphone *</Label>
                      <Input
                        id="guest-phone"
                        value={guestPhone}
                        onChange={(e) => setGuestPhone(e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        className="mt-1"
                        data-testid="guest-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="guest-email" className="text-sm text-gray-600">Email (optionnel, pour le reçu)</Label>
                      <Input
                        id="guest-email"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="mt-1"
                        data-testid="guest-email"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <Label className="font-semibold text-gray-900">
                  Instructions spéciales (optionnel)
                </Label>
                <Textarea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Allergies, préférences de préparation..."
                  className="mt-2"
                  data-testid="special-instructions"
                />
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Mode de paiement</h3>
                <RadioGroup value={paymentMethod} onValueChange={(v) => { 
                  if (onlinePaymentDisabled && v === 'Carte') return;
                  if (counterPaymentDisabled && v === 'À la récupération') return;
                  setPaymentMethod(v); 
                }}>
                  <label className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                    onlinePaymentDisabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : paymentMethod === 'Carte'
                        ? 'border-emerald-400 bg-emerald-50 cursor-pointer'
                        : 'border-gray-200 hover:border-emerald-300 cursor-pointer'
                  }`} data-testid="payment-option-card">
                    <RadioGroupItem value="Carte" id="card" disabled={onlinePaymentDisabled} />
                    <CreditCard className={`w-5 h-5 ${onlinePaymentDisabled ? 'text-gray-400' : paymentMethod === 'Carte' ? 'text-emerald-600' : 'text-gray-500'}`} />
                    <div className="flex-1">
                      <p className={`font-medium ${onlinePaymentDisabled ? 'line-through text-gray-400' : ''}`}>
                        Payer en ligne
                      </p>
                      <p className={`text-sm ${onlinePaymentDisabled ? 'text-red-400' : 'text-gray-500'}`}>
                        {onlinePaymentDisabled ? 'Non disponible pour ce restaurant' : 'Carte bancaire via Stripe'}
                      </p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-4 p-4 rounded-xl border transition-colors mt-2 ${
                    counterPaymentDisabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : paymentMethod === 'À la récupération'
                        ? 'border-emerald-400 bg-emerald-50 cursor-pointer'
                        : 'border-gray-200 hover:border-emerald-300 cursor-pointer'
                  }`} data-testid="payment-option-counter">
                    <RadioGroupItem value="À la récupération" id="pickup" disabled={counterPaymentDisabled} />
                    <Banknote className={`w-5 h-5 ${counterPaymentDisabled ? 'text-gray-400' : paymentMethod === 'À la récupération' ? 'text-emerald-600' : 'text-gray-500'}`} />
                    <div className="flex-1">
                      <p className={`font-medium ${counterPaymentDisabled ? 'line-through text-gray-400' : ''}`}>Payer sur place</p>
                      <p className={`text-sm ${counterPaymentDisabled ? 'text-red-400' : 'text-gray-500'}`}>
                        {counterPaymentDisabled ? counterDisabledReason : 'Especes ou carte a la recuperation'}
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-6 border border-gray-100 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-4">Récapitulatif</h3>
                
                {/* Promo Code Input */}
                <div className="mb-4 pb-4 border-b border-gray-100">
                  <Label className="text-sm text-gray-600 mb-2 block">Code promo</Label>
                  {appliedPromo ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-600" />
                        <span className="font-mono font-semibold text-emerald-700">{appliedPromo.code}</span>
                        <span className="text-sm text-emerald-600">
                          -{appliedPromo.discount_amount.toFixed(2)}€
                        </span>
                      </div>
                      <button
                        onClick={handleRemovePromo}
                        className="p-1 hover:bg-emerald-100 rounded-full"
                      >
                        <X className="w-4 h-4 text-emerald-600" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Entrez votre code"
                        className="font-mono uppercase"
                        onKeyDown={(e) => e.key === 'Enter' && handleValidatePromo()}
                      />
                      <Button
                        onClick={handleValidatePromo}
                        disabled={validatingPromo || !promoCode.trim()}
                        variant="outline"
                        className="shrink-0"
                      >
                        {validatingPromo ? (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin" />
                        ) : (
                          <Ticket className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sous-total</span>
                    <span className="font-medium">{cart.subtotal?.toFixed(2)} €</span>
                  </div>
                  
                  {/* Automatic Promotion (Happy Hour / Pack) */}
                  {autoPromotion?.has_promotion && (
                    <div className="flex justify-between text-amber-600 bg-amber-50 -mx-2 px-2 py-2 rounded-lg">
                      <span className="flex items-center gap-1">
                        {autoPromotion.promotion?.type === 'happy_hour' ? (
                          <Zap className="w-3 h-3" />
                        ) : (
                          <Gift className="w-3 h-3" />
                        )}
                        {autoPromotion.promotion?.name}
                      </span>
                      <span className="font-medium">-{autoPromotion.discount_amount?.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  {/* BOGO Offers Applied */}
                  {bogoOffers?.has_bogo && bogoOffers.applied_offers?.map((offer, idx) => (
                    <div key={idx} className="flex justify-between text-purple-600 bg-purple-50 -mx-2 px-2 py-2 rounded-lg">
                      <span className="flex items-center gap-1">
                        <ShoppingBag className="w-3 h-3" />
                        {offer.offer_name}
                        <span className="text-xs text-purple-500">
                          ({offer.free_item_quantity}x {offer.free_item_name} {offer.discount_type === 'free' ? 'offert' : `-${offer.discount_value}%`})
                        </span>
                      </span>
                      <span className="font-medium">-{offer.discount_amount?.toFixed(2)} €</span>
                    </div>
                  ))}
                  
                  {appliedPromo && (
                    <div className="flex justify-between text-emerald-600">
                      <span className="flex items-center gap-1">
                        <Ticket className="w-3 h-3" />
                        Réduction ({appliedPromo.code})
                      </span>
                      <span className="font-medium">-{appliedPromo.discount_amount.toFixed(2)} €</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between">
                    <span className="text-gray-500">Frais de service</span>
                    <span className="font-medium">{cart.service_fee?.toFixed(2)} €</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex justify-between text-base">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-emerald-600">
                      {((cart.subtotal || 0) - (autoPromotion?.discount_amount || 0) - (bogoOffers?.total_discount || 0) - (appliedPromo?.discount_amount || 0) + (cart.service_fee || 0)).toFixed(2)} €
                    </span>
                  </div>
                  
                  {/* Deposit (Acompte) Section */}
                  {depositPreview?.has_deposit && paymentMethod === 'Carte' && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                      <div className="flex items-center gap-2 text-amber-700">
                        <Wallet className="w-5 h-5" />
                        <span className="font-semibold">Acompte requis</span>
                      </div>
                      
                      {/* Deposit breakdown */}
                      {depositPreview.deposit_items?.length > 0 && (
                        <div className="text-xs text-amber-600 space-y-1">
                          {depositPreview.deposit_items.map((item, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>{item.dish_name}</span>
                              <span>
                                {item.deposit_type === 'percentage' 
                                  ? `${item.deposit_value}%` 
                                  : `${item.deposit_value.toFixed(2)}€`} = {item.deposit_amount.toFixed(2)}€
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="border-t border-amber-200 pt-3 space-y-2">
                        <div className="flex justify-between text-amber-800">
                          <span className="font-medium flex items-center gap-1">
                            <CreditCard className="w-4 h-4" />
                            À payer maintenant
                          </span>
                          <span className="font-bold text-lg">{depositPreview.deposit_amount?.toFixed(2)} €</span>
                        </div>
                        <div className="flex justify-between text-amber-600">
                          <span className="flex items-center gap-1">
                            <Banknote className="w-4 h-4" />
                            Reste à payer sur place
                          </span>
                          <span className="font-semibold">{depositPreview.remaining_amount?.toFixed(2)} €</span>
                        </div>
                      </div>
                      
                      {/* Refund policy */}
                      {depositPreview.refund_policy && (
                        <div className="flex gap-2 p-2 bg-amber-100 rounded-lg text-xs text-amber-700">
                          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium">Politique de remboursement</p>
                            <p>{depositPreview.refund_policy}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleSubmitOrder}
                  disabled={loading || !selectedSlot || !paymentMethod || ((isPreorderCart || pickupMode === 'reservation') && !selectedReservationDate)}
                  className={`w-full mt-6 h-12 rounded-full font-semibold ${
                    !paymentMethod
                      ? 'bg-gray-300 cursor-not-allowed'
                      : isPreorderCart 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                  data-testid="confirm-order-button"
                >
                  {loading ? 'Traitement...' : (
                    isPreorderCart ? (
                      paymentMethod === 'Carte' 
                        ? 'Confirmer la pré-commande (paiement en ligne)'
                        : 'Confirmer la pré-commande'
                    ) : paymentMethod === 'Carte' ? (
                      depositPreview?.has_deposit 
                        ? `Payer l'acompte (${depositPreview.deposit_amount?.toFixed(2)} €)` 
                        : 'Payer maintenant'
                    ) : 'Confirmer la commande'
                  )}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>

                <button
                  onClick={() => clearCart()}
                  className="w-full mt-3 text-sm text-gray-500 hover:text-red-500"
                >
                  Vider le panier
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
